"use strict";

// Copyright 2010-2020 The MathWorks, Inc.

/**
 * This is the main entry point to the remote layer. It instantiates all the objects and presents
 * a facade API for consumers to use (data services).
 */
define(['dojo/_base/lang', 'dojo/aspect', './iframeproxy/IframeProxyClient', './impl/FaultManager', './impl/RequestManager', './impl/ResponseManager', './impl/Transaction', './impl/AutoDetectedTransaction', './RemoteEndPointsManager', './utils/SendMessageUtil', './UploadToWorker', 'mw-log/Log', './serviceRegistryLookup!'], function (lang, aspect, IframeProxyClient, FaultManager, RequestManager, ResponseManager, Transaction, AutoDetectedTransaction, RemoteEndPointsManager, SendMessageUtil, UploadToWorker, Log, serviceRegistryLookup) {
  var remote; // use a setup method to help unit tests reset this singleton.

  remote = {
    reset: function reset() {
      var allowedMessages = [];
      var faultManager = new FaultManager();
      var responseManager = new ResponseManager({
        faultManager: faultManager
      });
      var requestManager = new RequestManager({
        responseManager: responseManager
      });
      remote.faultManager = faultManager;
      remote.responseManager = responseManager;
      remote.requestManager = requestManager;
      remote._serviceRegistryLookup = serviceRegistryLookup;
      remote._uploadIframeProxyUrlParameters = ['?computeToken=', '', '&routingkey=', ''];
      var proxyClient = remote.proxyClient = new IframeProxyClient();
      remote.createUploadIframeTarget = lang.hitch(proxyClient, proxyClient.createUploadIframeTarget);
      remote.cancelUpload = lang.hitch(proxyClient, proxyClient.cancelUpload);
      RemoteEndPointsManager.setIframeProxyClientForEndPoint(RemoteEndPointsManager.getEndPointNames().ROUTER_LEAF_SECURE_ENDPOINT, proxyClient);
      RemoteEndPointsManager.setIframeProxyClientForEndPoint(RemoteEndPointsManager.getEndPointNames().ROUTER_LEAF_INSECURE_ENDPOINT, proxyClient); // todo: have some way to select the transaction implementation. for example, use XHR for
      // MatlabFacade.

      Transaction.construct = function (args) {
        args = args || {};

        if (!RemoteEndPointsManager.getRoutingHost(RemoteEndPointsManager.getEndPointNames().ROUTER_LEAF_INSECURE_ENDPOINT)) {
          remote.setEndpoint();
        }

        args.allowedMessages = allowedMessages;
        args.sendMessage = remote.sendMessage;
        args.computeSessionId = remote.getComputeSessionId;
        return new AutoDetectedTransaction(args);
      };
      /**
       * Function gives flexibility to add top level fault handling per iframeProxyClient.
       * IframeProxyClient's onError gets invoked when there is 400 level or 500 level errors.
       * @param serviceEndPoint
       * @param faultHandler
       * @param scope
       */


      remote.registerEndpointLevelFaultHandler = lang.hitch(faultManager, faultManager.registerEndpointLevelFaultHandler); // create a facade on the remote object for easy access to the main api of the remote layer.
      // Send a message to the server

      remote.sendMessage = lang.hitch(requestManager, requestManager.sendMessage); // Send Payload to the server, used my mw-messageservice for a Transactionless way to send the data to the server.

      remote.sendPayload = SendMessageUtil.sendPayload; // Register a default request factory function

      remote.registerDefaultMessageFactory = lang.hitch(requestManager, requestManager.registerDefaultMessageFactory);
      remote.deregisterDefaultMessageFactory = lang.hitch(requestManager, requestManager.deregisterDefaultMessageFactory); // Register a response handler for a specific type

      remote.registerResponseHandler = lang.hitch(responseManager, responseManager.registerResponseHandler);
      remote.deregisterResponseHandler = lang.hitch(responseManager, responseManager.deregisterResponseHandler); // Register a response handler which is called when the specified type is in the request
      // which resulted in an fault

      remote.registerFaultHandler = lang.hitch(faultManager, faultManager.registerFaultHandler);
      remote.deregisterFaultHandler = lang.hitch(faultManager, faultManager.deregisterFaultHandler); // Register a response handler which is called when the specified fault code matches

      remote.registerTopLevelFaultHandler = lang.hitch(faultManager, faultManager.registerTopLevelFaultHandler);
      remote.deregisterTopLevelFaultHandler = lang.hitch(faultManager, faultManager.deregisterTopLevelFaultHandler);
      /**
       * This function is used to set routingHost and Key for other services like
       * USAGE_DATA_ROUTER_ENDPOINT
       * @param serviceName : This is the service Name. Currently supported serviceNames are
       * following
       * USAGE_DATA_ROUTER_ENDPOINT | WORKER_ENDPOINT | ROUTER_LEAF_SECURE_ENDPOINT |
       * ROUTER_LEAF_INSECURE_ENDPOINT
       * @param routingHost
       * @param routingKey
       */

      remote.setServiceEndPoint = function (serviceName, routingHost, routingKey) {
        RemoteEndPointsManager.setRoutingHostAndRoutingKey(serviceName, routingHost, routingKey);
      };
      /**
       * API to enable direct XHR communication
       */


      remote.setDirectXhrCommunicationState = function (flag) {
        RemoteEndPointsManager._setDirectXhrCommunicationState(flag);
      };
      /**
       * This function is used to set pathSuffix for other services like HELP_DATA_ROUTER_ENDPOINT
       * @param serviceName : This is the service Name. Currently supported serviceNames are
       * following HELP_DATA_ROUTER_ENDPOINT
       * @param serviceName
       * @param pathSuffix
       */


      remote.setServicePathSuffixForEndPoint = function (serviceName, pathSuffix) {
        RemoteEndPointsManager.setPathSuffixForEndPoint(serviceName, pathSuffix);
      }; // Ignore faults that match the specified fault code.


      remote.ignoreTopLevelFault = remote.registerTopLevelFaultHandler; // Set a compute token to be sent with all requests

      remote.setComputeToken = function (token) {
        remote._uploadIframeProxyUrlParameters[1] = token.computeSessionId;
        requestManager.computeToken = token;
      }; // todo: simplify the endpoint management. we have worker, leaf, router, worker routing key


      remote.setWorkerEndpoint = function (newRoutingHost, newRoutingKey) {
        remote._uploadIframeProxyUrlParameters[3] = newRoutingKey;
        RemoteEndPointsManager.setRoutingHostAndRoutingKey(RemoteEndPointsManager.getEndPointNames().WORKER_ENDPOINT, newRoutingHost, newRoutingKey);
      }; // Set the base endpoint to use for all transactions


      remote.setEndpoint = function (newEndpoint) {
        if (!newEndpoint) {
          var endpoint = remote._serviceRegistryLookup.getServiceRegistryEndpoint('primary');

          if (endpoint) {
            newEndpoint = endpoint;
          } else {
            Log.info('ServiceRegistryLookup is empty, falling back to localhost');
            newEndpoint = '/';
          }
        }

        if (!newEndpoint || newEndpoint === '/') {
          var baseHref = (document.getElementsByTagName('base')[0] || {}).href;

          if (baseHref !== undefined) {
            var fakeLink = document.createElement('a');
            fakeLink.display = 'none';
            fakeLink.href = baseHref;
            newEndpoint = fakeLink.protocol + '//' + fakeLink.hostname + (fakeLink.port ? ':' + fakeLink.port : '') + '/';
          } else {
            newEndpoint = window.location.protocol + '//' + window.location.host + '/';
          }
        } // Set both secure's and insecure's host name


        RemoteEndPointsManager.setRoutingHostAndRoutingKey(RemoteEndPointsManager.getEndPointNames().ROUTER_LEAF_SECURE_ENDPOINT, newEndpoint);
        RemoteEndPointsManager.setRoutingHostAndRoutingKey(RemoteEndPointsManager.getEndPointNames().ROUTER_LEAF_INSECURE_ENDPOINT, newEndpoint);
        proxyClient.setRemotePageHost(newEndpoint);
      };

      remote.setAllowedMessages = function (newAllowedMessages) {
        allowedMessages = newAllowedMessages;
      };

      remote.allowAllMessages = function () {
        allowedMessages = [];
      }; // Though it says endPoint, it actually means the Router/Leaf Host name.
      // TODO change the name here.


      remote.getEndPoint = function () {
        return RemoteEndPointsManager.getRoutingHost(RemoteEndPointsManager.getEndPointNames().ROUTER_LEAF_SECURE_ENDPOINT);
      };

      remote.getUploadIframeProxyUrl = function () {
        var routingHost = remote.getWorkerRoutingHost();
        var lastChar = routingHost.slice(-1);

        if (lastChar === '/') {
          routingHost = routingHost.slice(0, -1);
        } // need to remove the last '/' from the url

        /*
            In 17b, we are uploading to worker router directly.
            To be able load the upload iframe proxy from MCG we need to pass routingkey
            and computeToken
         */


        return routingHost + proxyClient.getUploadIframeProxyUrlPath() + remote._uploadIframeProxyUrlParameters.join('');
      };

      remote.getComputeSessionId = function () {
        return requestManager.computeToken ? requestManager.computeToken.computeSessionId : null;
      };
      /**
       * Returns routingHost set during WRA workflow. Else, returns current location
       */


      remote.getWorkerRoutingHost = function () {
        var workerRoutingHost = RemoteEndPointsManager.getRoutingHost(RemoteEndPointsManager.getEndPointNames().WORKER_ENDPOINT);
        /**
         * Expectation:
         * We use mre query parameter to configure Remote layer's "primary" endpoint.
         * This endpoint signifies where to find MATLAB
         * If Worker endpoint has been set already, then we want that to be returned.
         * However, if not, we want the value of "primary" endpoint to be returned
         * And if even that is not configured, then return the URL where Remote layer is being served from
         * should be returned
         * Reason:
         * On Desktop/localhost: primary endpoint is also the Worker endpoint
         * On Cloud stacks, the "mre" query parameter is used to provide information on where MATLAB is
         * by setting the "primary" endpoint so we want that information returned if it exists, instead
         * of where Remote is hosted
         */

        if (workerRoutingHost) {
          return workerRoutingHost;
        } else {
          workerRoutingHost = remote._serviceRegistryLookup.getServiceRegistryEndpoint('primary'); // If the serviceregistry has primary endpoint as "/" and no mre query parameter was passed then we get "/" back
          // However, This is not a valid value and should be translated to https://localhost:31515/

          if (!workerRoutingHost || workerRoutingHost === '/') {
            var baseHref = (document.getElementsByTagName('base')[0] || {}).href;

            if (baseHref !== undefined) {
              var fakeLink = document.createElement('a');
              fakeLink.display = 'none';
              fakeLink.href = baseHref;
              workerRoutingHost = fakeLink.protocol + '//' + fakeLink.hostname + (fakeLink.port ? ':' + fakeLink.port : '') + '/';
            } else {
              workerRoutingHost = window.location.protocol + '//' + window.location.host + '/';
            }
          }

          return workerRoutingHost;
        }
      };
      /**
       * Return routingKey set during WRA workflow. Else returns default routingKey ,
       * This is used by MessageServiceAsync to get the routingKey and routing Host set. These
       * are either unset or set to worker endPoints. Hence This method explicitly returns the
       * workerEndPointRoutingKey
       */


      remote.getWorkerRoutingKey = function () {
        return RemoteEndPointsManager.getRoutingKey(RemoteEndPointsManager.getEndPointNames().WORKER_ENDPOINT);
      };
      /**
       * Upload artifacts provided to Worker
       *
       * @param {FileList} artifactsList - List of files that have to be uploaded
       * @param {string} destination - File system location where the file has to be uploaded
       * @return {*} a deferred
       */


      remote.upload = function (artifactsList, destination) {
        return new UploadToWorker().upload(artifactsList, destination, {
          endpoint: remote.getWorkerRoutingHost(),
          uploadIframeProxyUrl: remote.getUploadIframeProxyUrl()
        });
      };
      /**
       * Returns the endpoint from: /serviceregistrylookup/1/service
       * If endpoint does not exist or the file is non-existant returns
       * it returns "" otherwise an absolute OR root relative URL.
       *
       * @param {string} name - Name of the ServiceRegistry key
       * @returns {string} - absolute OR root relative endpoint URL
       */


      remote.getServiceRegistryEndpoint = function (name) {
        return remote._serviceRegistryLookup.getServiceRegistryEndpoint(name);
      };
      /**
      * @desc Turns on Server on Worker Async - Which is on by default
      * And should never be off
      * @deprecated Planning on removal - no one should be using this. g1219276
      * @returns {void}
      */


      remote.enableServerOnWorkerAsync = function () {
        RemoteEndPointsManager.enableEndPoint(RemoteEndPointsManager.getEndPointNames().WORKER_ASYNC_ENDPOINT);
      };
      /**
      * @desc Turns off Server on Worker Async - Which is on by default
      * And should never be off
      * @deprecated Planning on removal - no one should be using this. g1219276
      * @returns {void}
      */


      remote.disableServerOnWorkerAsync = function () {
        RemoteEndPointsManager.disableEndPoint(RemoteEndPointsManager.getEndPointNames().WORKER_ASYNC_ENDPOINT);
      };
      /**
      * @desc Checks status of Server on Worker Async- Which is on by default
      * And should never be off
      * @deprecated Planning on removal - no one should be using this. g1219276
      * @returns {void}
      */


      remote.isServerOnWorkerAsyncEnabled = function () {
        return RemoteEndPointsManager.isEndPointEnabled(RemoteEndPointsManager.getEndPointNames().WORKER_ASYNC_ENDPOINT);
      };
      /**
      * @desc enableServerOnWorker - turns on Server on Worker
      * This *should* be turned on by Default and is likely
      * only being called by MOTW.
      * @deprecated Planning on removal, and setting WORKER_ENDPONT true by default. g1219276
      * @returns {void}
      */


      remote.enableServerOnWorker = function () {
        RemoteEndPointsManager.enableEndPoint(RemoteEndPointsManager.getEndPointNames().WORKER_ENDPOINT);
      };
      /**
      * @desc disableServerOnWorker - turns off Server on Worker
      * This *should* be turned on by Default and is likely never called by anyone
      * @deprecated Planning on removal, and setting WORKER_ENDPONT true by default. g1219276
      * @returns {void}
      */


      remote.disableServerOnWorker = function () {
        RemoteEndPointsManager.disableEndPoint(RemoteEndPointsManager.getEndPointNames().WORKER_ENDPOINT);
      };
      /**
      * @desc isWorkerEndpointEnabled - Checks to see status of server on worker
      * This *should* be turned on by Default and is likely
      * only being called by MOTW.
      * @deprecated Planning on removal, and setting WORKER_ENDPONT true by default. g1219276
      * @returns {void}
      */


      remote.isWorkerEndpointEnabled = function () {
        return RemoteEndPointsManager.isEndPointEnabled(RemoteEndPointsManager.getEndPointNames().WORKER_ENDPOINT);
      };

      remote.setWorkerOnlyMessageTypes = function (messages) {
        RemoteEndPointsManager.setMessageTypesToEndPoint(RemoteEndPointsManager.getEndPointNames().WORKER_ENDPOINT, messages);
      };

      aspect.after(proxyClient, 'onError', function (fault) {
        if (fault) {
          faultManager.handleNonTransactionFault(fault);
        }
      }, true);
      /**
       * This method can be used to create an endPoint during runtime.
       * @param endPointName : Name of the endPoint
       * @param isEnabled : During the initialization, the endPoint can be enabled or disabled
       * using this param
       * @param pathSuffix : This is the suffix of the URL to which we post messages.
       * @param IframeProxyName: Name of the iframe created.
       * @param isMessageContainerBased: This endpoint can be used to determine if it is message container Based or not
       */

      remote.createNewEndPoint = function (endPointName, isEnabled, pathSuffix, IframeProxyName, isMessageContainerBased) {
        var endPoint = this.getEndPointByName(endPointName);

        if (!endPoint) {
          RemoteEndPointsManager.setUpNewEndPoint(endPointName, isEnabled, pathSuffix, IframeProxyName, isMessageContainerBased);
        }
      };
      /**
       * Returns complete URL to which we must post messages.
       * @param endPointName
       * @return {*} String
       */


      remote.getUrlForEndPoint = function (endPointName) {
        return RemoteEndPointsManager.getRemoteUrlForEndPoint(endPointName);
      };
      /**
       * This method returns the endPointName from the the EndPoint Collection of EndPointManager
       * @returns {*} String
      */


      remote.getEndPointByName = function (endpointName) {
        return RemoteEndPointsManager.getEndPointNames()[endpointName];
      };
      /**
       * This function is used to get the directXhr for the end point
       * @param endPointName
       * @return {*} Deferred
       */


      remote.getEndPointAllowsDirectXhr = function (endPoint) {
        return RemoteEndPointsManager.getEndPointAllowsDirectXhr(endPoint);
      };
    }
  };
  remote.reset();
  return remote;
});
//# sourceMappingURL=Remote.js.map
